import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Loader2, Globe, Users, Clock, Share2, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const toLocalWave = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", timeZoneName: "short",
    });
  } catch {
    return iso;
  }
};

const useCountdown = (iso) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor((diff % 86_400_000) / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1_000);
  return { d, h, m, s, done: diff === 0 };
};

const Night = () => {
  const { t, locale } = useTranslation();
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").toUpperCase();
  const [state, setState] = useState({ loading: true, event: null, count: 0 });
  const [form, setForm] = useState({ email: "", nickname: "", country: "", crew: "" });
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(null); // {ref_code, share_url}

  useEffect(() => {
    api.get("/night").then((r) => {
      setState({ loading: false, event: r.data.event, count: r.data.signup_count });
    }).catch(() => {
      setState({ loading: false, event: null, count: 0 });
      toast.error("Couldn't load the event. Try again in a moment.");
    });
    api.post("/events", { event: "gmn_page_viewed", props: { ref: refCode || null } }).catch(() => {});
  }, [refCode]);

  // Returning visitor — if they're already signed up, show a "resume" link.
  const existingSignupId = typeof window !== "undefined" ? localStorage.getItem("gmn_signup_id") : null;

  const countdown = useCountdown(state.event?.waves?.[0]?.starts_at_utc);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/night/signup", { ...form, ref_code: refCode });
      setSignedUp({ ref_code: data.ref_code, share_url: data.share_url, signup_id: data.signup_id });
      localStorage.setItem("gmn_signup_id", data.signup_id);
      setState((s) => ({ ...s, count: s.count + 1 }));
      toast.success("You're in. Welcome, detective.");
      api.post("/events", { event: "gmn_signup", props: { has_crew: !!form.crew, has_country: !!form.country, referred_by: refCode || null } }).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const shareNative = async (shareUrl) => {
    const text = state.event?.share_text || "Join me on wasntme.party Global Mystery Night.";
    const url = shareUrl || `${window.location.origin}/night`;
    if (navigator.share) {
      try {
        await navigator.share({ title: state.event?.title || "Global Mystery Night", text, url });
        api.post("/events", { event: "gmn_shared", props: { via: "native" } }).catch(() => {});
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Share text copied — paste anywhere.");
      api.post("/events", { event: "gmn_shared", props: { via: "clipboard" } }).catch(() => {});
    }
  };

  const shareWhatsApp = (shareUrl) => {
    const text = state.event?.share_text || "Join me on wasntme.party Global Mystery Night.";
    const url = shareUrl || `${window.location.origin}/night`;
    const w = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
    window.open(w, "_blank", "noopener,noreferrer");
    api.post("/events", { event: "gmn_shared", props: { via: "whatsapp" } }).catch(() => {});
  };

  if (state.loading) {
    return <div className="grain min-h-screen flex items-center justify-center text-[#a39797]"><Loader2 className="animate-spin" /></div>;
  }
  if (!state.event) {
    return <div className="grain min-h-screen flex items-center justify-center p-6 text-[#ff6b6b]">Event unavailable right now.</div>;
  }

  const e = state.event;
  const accent = e.accent_hex || "#8b0000";
  const dateLabel = e.waves?.[0]?.starts_at_utc
    ? new Date(e.waves[0].starts_at_utc).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "Date TBA";

  return (
    <div className="grain min-h-screen" data-testid="gmn-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37]" data-testid="gmn-back">
            <ArrowLeft size={16} /> {t("common.backToFile")}
          </Link>
          <LanguageSwitcher />
        </div>

        {/* Hero */}
        <div className="font-hand rotate-[-1deg] text-2xl mb-2" style={{ color: accent }}>
          {t("night.sectionLabel")} · <span translate="no">{dateLabel}</span>
        </div>
        <h1 className="font-display text-4xl sm:text-6xl mb-5" translate="no">{e.title}</h1>
        <p className="text-[#c7bebe] text-lg mb-6 max-w-2xl leading-relaxed">{e.tagline}</p>
        <p className="text-[#a39797] mb-8 max-w-2xl leading-relaxed">{e.hero_copy}</p>

        {/* Countdown */}
        {countdown && !countdown.done && (
          <div className="card-noir p-5 mb-8 inline-flex items-center gap-6" data-testid="gmn-countdown"
               style={{ borderColor: accent + "80" }}>
            <Clock size={20} style={{ color: accent }} />
            <div className="flex items-center gap-5">
              {["d", "h", "m", "s"].map((k) => (
                <div key={k} className="text-center">
                  <div className="font-display text-3xl text-[#f4f0ec]">{String(countdown[k]).padStart(2, "0")}</div>
                  <div className="text-[10px] tracking-widest text-[#a39797] uppercase">
                    {{ d: "days", h: "hrs", m: "min", s: "sec" }[k]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Social proof */}
        <div className="flex items-center gap-6 mb-6 text-sm text-[#a39797] flex-wrap">
          <div className="flex items-center gap-2"><Users size={14} /> <span data-testid="gmn-count"><b className="text-[#f4f0ec]" translate="no">{state.count}</b> {t("night.detectivesCount")}</span></div>
          <div className="flex items-center gap-2"><Globe size={14} /> 3 {t("night.wavesWorldwide")}</div>
          {existingSignupId && !signedUp && (
            <Link to={`/night/play/${existingSignupId}`} className="btn-ghost text-xs"
              data-testid="gmn-resume" style={{ borderColor: accent }}>
              {t("night.resumeLink")}
            </Link>
          )}
        </div>

        {/* Signup OR Confirmation */}
        {!signedUp ? (
          <form onSubmit={submit} className="card-noir p-6 space-y-4" style={{ borderColor: accent }} data-testid="gmn-signup-form">
            <div className="text-xs tracking-widest text-[#a39797]">{t("night.reserveFormLabel")}</div>
            <input type="email" required className="input-noir" placeholder={t("night.emailPlaceholder")}
              value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="gmn-email" />
            <div className="grid md:grid-cols-2 gap-3">
              <input className="input-noir" placeholder={t("night.nicknamePlaceholder")}
                value={form.nickname} onChange={(e) => setForm((f) => ({ ...f, nickname: e.target.value }))}
                data-testid="gmn-nickname" />
              <input className="input-noir" placeholder={t("night.countryPlaceholder")}
                value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                data-testid="gmn-country" />
            </div>
            <input className="input-noir" placeholder={t("night.crewPlaceholder")}
              value={form.crew} onChange={(e) => setForm((f) => ({ ...f, crew: e.target.value }))}
              data-testid="gmn-crew" />
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50"
              data-testid="gmn-submit" style={{ backgroundColor: accent }}>
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Skull size={16} />}
              {submitting ? t("night.reserving") : (locale === "en" && e.cta_label ? e.cta_label : t("night.reserveBtn"))}
            </button>
            <div className="text-xs text-[#a39797]">
              {t("night.reserveDisclaimer")}
              {refCode && <span> {t("night.invitedBy")} <b translate="no">{refCode}</b>.</span>}
            </div>
          </form>
        ) : (
          <div className="card-noir p-6" style={{ borderColor: accent }} data-testid="gmn-confirmation">
            <div className="flex items-center gap-3 mb-3">
              <Check size={24} className="text-[#4ade80]" />
              <div className="font-display text-2xl">{t("night.youreInHeader")}</div>
            </div>
            <p className="text-[#a39797] mb-5">{t("night.youreInBody")}</p>
            <div className="bg-[#1a1413] border border-[#2d2424] rounded p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs tracking-widest text-[#a39797]">{t("night.yourInviteLink")}</div>
                <div className="font-mono text-sm text-[#f4f0ec] break-all" translate="no">{signedUp.share_url}</div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(signedUp.share_url); toast.success(t("common.copied")); }}
                className="btn-ghost text-xs" data-testid="gmn-copy-link">
                {t("common.copy").toLowerCase()}
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/night/play/${signedUp.signup_id}`} className="btn-primary text-sm"
                data-testid="gmn-play-link" style={{ backgroundColor: accent }}>
                <Skull size={14} /> {t("night.openDetectivePass")}
              </Link>
              <button onClick={() => shareWhatsApp(signedUp.share_url)} className="btn-ghost text-sm"
                data-testid="gmn-share-whatsapp">
                <MessageCircle size={14} /> {t("night.shareWhatsApp")}
              </button>
              <button onClick={() => shareNative(signedUp.share_url)} className="btn-ghost text-sm"
                data-testid="gmn-share-native">
                <Share2 size={14} /> {t("night.shareNative")}
              </button>
              <a href={`/api/night/share-card.png?nickname=${encodeURIComponent(form.nickname)}&country=${encodeURIComponent(form.country)}`}
                target="_blank" rel="noreferrer" className="btn-ghost text-sm"
                data-testid="gmn-share-card">
                {t("night.downloadCard")}
              </a>
            </div>
            <div className="text-xs text-[#a39797] mt-4">
              {t("night.referralBlurb")}
            </div>
          </div>
        )}

        {/* Crew Night upgrade — paid tier on top of the free event */}
        {signedUp && (
          <CrewUpgrade signupId={signedUp.signup_id} accent={accent} />
        )}

        {/* How it works */}
        <div className="mt-14">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">{t("night.howItWorksLabel")}</div>
          <h2 className="font-display text-2xl mb-5">{t("night.howItWorksTitle")}</h2>
          <ol className="space-y-3 text-[#c7bebe]">
            {(e.how_it_works || []).map((line, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-display text-xl" style={{ color: accent }}>{i + 1}.</span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Waves */}
        <div className="mt-14">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">{t("night.pickWaveLabel")}</div>
          <h2 className="font-display text-2xl mb-5">{t("night.pickWaveTitle")}</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {e.waves.map((w) => (
              <div key={w.id} className="card-noir p-4" data-testid={`gmn-wave-${w.id}`}>
                <div className="text-xs tracking-widest text-[#a39797] mb-2">{w.name}</div>
                <div className="font-display text-lg text-[#f4f0ec]" translate="no">{toLocalWave(w.starts_at_utc)}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-[#a39797] mt-3">
            {t("night.pickWaveFooter")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Night;

// =============================================================================
// Crew Night upgrade — paid tier picker shown after free signup confirms.
// =============================================================================
const CrewUpgrade = ({ signupId, accent }) => {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);

  useEffect(() => {
    api.get("/night/crew/tiers").then((r) => {
      setTiers(r.data.tiers || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const upgrade = async (tier) => {
    setChecking(tier);
    try {
      const { data } = await api.post("/night/crew/checkout", {
        signup_id: signupId, tier,
        origin_url: window.location.origin,
      });
      if (data.status === "already_active" && data.room_url) {
        window.location.href = data.room_url;
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upgrade failed. Try again.");
      setChecking(null);
    }
  };

  if (loading) return null;

  return (
    <div className="mt-10" data-testid="crew-upgrade">
      <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">{t("crew.sectionLabel")}</div>
      <h2 className="font-display text-2xl mb-2">{t("crew.title")}</h2>
      <p className="text-[#a39797] text-sm mb-5 max-w-2xl">
        {t("crew.intro")}
      </p>
      <div className="grid md:grid-cols-3 gap-4">
        {tiers.map((cfg, i) => (
          <div
            key={cfg.id}
            className="card-noir p-5 relative flex flex-col"
            style={{ borderColor: i === 1 ? accent : undefined }}
            data-testid={`crew-tier-${cfg.id}`}
          >
            {i === 1 && (
              <div className="absolute -top-3 left-5 text-[10px] tracking-widest px-2 py-1 rounded"
                style={{ backgroundColor: accent, color: "#f8f3e4" }}>
                {t("crew.mostPopular")}
              </div>
            )}
            <div className="text-xs tracking-widest text-[#a39797] mb-1">{cfg.label.toUpperCase()}</div>
            <div className="font-display text-4xl mb-3" style={{ color: accent }}>
              <span translate="no">{cfg.amount_label}</span>
              <span className="text-xs text-[#a39797] ml-2">{t("crew.perCrew")}</span>
            </div>
            <ul className="space-y-2 text-sm text-[#c7bebe] mb-5 flex-1">
              {cfg.perks.map((p, j) => (
                <li key={j} className="flex gap-2">
                  <Check size={14} className="text-[#4ade80] shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => upgrade(cfg.id)}
              disabled={!!checking}
              className="btn-primary justify-center w-full"
              style={{ backgroundColor: accent }}
              data-testid={`crew-upgrade-${cfg.id}-btn`}
            >
              {checking === cfg.id ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
              {checking === cfg.id ? t("crew.redirecting") : `${t("crew.upgradeBtn")} — ${cfg.amount_label}`}
            </button>
          </div>
        ))}
      </div>
      <div className="text-xs text-[#a39797] mt-4">
        {t("crew.footerNote")}
      </div>
    </div>
  );
};
